import { AutoStack, TimeAgo } from 'components'
import { dateTimeToGraphQLDateTime } from 'components/DatePickers/helpers'
import { DocumentGenerator } from 'components/DocumentGenerator/documentGenerator'
import { useDownloadBlobLinkMutation } from 'components/DownloadLink/DownloadLink.generated'
import { useCreateMailMutation } from 'components/Mailbox/Mailbox.generated'
import {
  FormikMailingListPicker,
  FormikSingleUserPicker,
} from 'components/PersonPicker'
import { Form } from 'formik'
import { FieldStack, FieldStackItem, Stack } from 'forms/FormStack'
import { Box } from 'grid'
import { useCurrentProving } from 'hooks/useCurrentProving'
import {
  FileTemplateType,
  InternalMemoState,
  LinkType,
  MailFolder,
  ProvingUpdateType,
} from 'model'
import { DefaultButton, Label, Separator, Text } from 'office-ui-fabric-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { isNullOrUndefined } from 'util'
import { useUpdateProvingMutation } from 'views/ProvingDetailsView/hooks'
import { Section } from 'views/ProvingDetailsView/Layout'

export type ProvingDetailsInternalMemoFormValues = Pick<
  ProvingUpdateType,
  'internalMemo'
>

export const ProvingDetailsInternalMemoForm: React.FC = () => {
  const { t } = useTranslation()
  const [proving, refetch] = useCurrentProving()
  const [testAddress, setTestAddress] = React.useState(() =>
    proving.internalMemo && proving.internalMemo.testRecipient
      ? proving.internalMemo.testRecipient.email
      : undefined
  )
  const [recipientAddresses, setRecipientAddresses] = React.useState<string[]>(
    proving.internalMemo && proving.internalMemo.mailingList
      ? proving.internalMemo.mailingList.map((p) => p.email || '')
      : []
  )
  const [testMailSent, setTestMailSent] = React.useState(false)
  const [internalMemoSent, setInternalMemoSent] = React.useState<boolean>(
    () =>
      !!(
        proving.internalMemo &&
        proving.internalMemo.state === InternalMemoState.Sent
      )
  )

  const [createMail] = useCreateMailMutation()
  const [downloadBlobLink] = useDownloadBlobLinkMutation()
  const [updateProving] = useUpdateProvingMutation()

  const handleSendMail = React.useCallback(
    (isTestMail: boolean) => async () => {
      if (proving.internalMemo && proving.internalMemo.memo) {
        const blobLinkRes = await downloadBlobLink({
          variables: {
            uri: proving.internalMemo.memo.uri,
            expiresIn: 7776000,
            linkType: LinkType.Shared,
          },
        })

        const link = blobLinkRes.data
          ? blobLinkRes.data.downloadBlobLink
          : undefined

        const mailTo = isTestMail
          ? [{ address: testAddress || '' }]
          : recipientAddresses.map((addr) => ({ address: addr }))

        if (link) {
          await createMail({
            variables: {
              provingId: proving.id,
              input: {
                folder: MailFolder.Outbox,
                to: mailTo,
                subject: `TestMail: Hausmitteilung zu Erprobung ${proving.id}`,
                body: `Link zur Hausmitteilung: <a href='${link}'>Download</a>`,
              },
            },
          })
        }
        if (isTestMail) {
          setTestMailSent(true)
        } else {
          await updateProving({
            variables: {
              id: proving.id,
              update: {
                internalMemo: {
                  sentOn: dateTimeToGraphQLDateTime(new Date()),
                  state: InternalMemoState.Sent,
                },
              },
            },
          })
          setInternalMemoSent(true)
          refetch()
        }
      }
    },
    [
      createMail,
      proving,
      testAddress,
      downloadBlobLink,
      recipientAddresses,
      updateProving,
      refetch,
    ]
  )

  return (
    <Form>
      <Section label={`1. ${t('section.flows.specifyMailingList')}`}>
        <FieldStack>
          <FieldStackItem
            name="internalMemo.mailingList"
            render={(fieldProps) => (
              <Stack.Item className="composite-field">
                <Label>{t('Verteiler')}</Label>
                <FormikMailingListPicker
                  onChange={(recipients) => {
                    if (recipients) {
                      const mailAddresses: string[] = []
                      recipients.forEach((rec) =>
                        mailAddresses.push(rec.secondaryText!)
                      )
                      setRecipientAddresses(mailAddresses)
                    }
                  }}
                  {...fieldProps}
                />
              </Stack.Item>
            )}
          />
        </FieldStack>
      </Section>
      <Section label={`2. ${t('section.flows.createInternalMemo')}`}>
        <FieldStack>
          <Stack.Item className="composite-field">
            <Label>{t('Hausmitteilung')}</Label>
            <FieldStackItem
              name="internalMemo.memo"
              templateType={FileTemplateType.InternalMemo}
              render={(fieldProps) => (
                <DocumentGenerator
                  templateType={FileTemplateType.InternalMemo}
                  fieldPath={fieldProps.field.name}
                  getUpdate={(doc) => ({
                    internalMemo: {
                      memo: doc,
                    },
                  })}
                  {...fieldProps}
                />
              )}
            />
          </Stack.Item>
        </FieldStack>
      </Section>
      <Section label={`3. ${t('section.flows.sendInternalMemo')}`}>
        <FieldStack>
          <Stack.Item className="composite-field">
            <Label styles={{ root: { overflowY: 'visible' } }}>
              {t(`Test E-Mail Empf.`)}
            </Label>
            <AutoStack tokens={{ childrenGap: 8 }}>
              <FieldStackItem
                name="internalMemo.testRecipient"
                render={(fieldProps) => (
                  <FormikSingleUserPicker
                    onChange={(items) =>
                      setTestAddress(
                        items && items[0] ? items[0].secondaryText : undefined
                      )
                    }
                    {...fieldProps}
                  />
                )}
              />
              {!testMailSent && (
                <DefaultButton
                  text={t('Test E-Mail versenden')}
                  onClick={handleSendMail(true)}
                  disabled={
                    testAddress === undefined ||
                    isNullOrUndefined(proving.internalMemo) ||
                    isNullOrUndefined(proving.internalMemo!.memo) ||
                    isNullOrUndefined(proving.internalMemo!.memo!.uri)
                  }
                />
              )}
              {testMailSent && <Box>{t('Test E-Mail wurde versendet!')}</Box>}
            </AutoStack>
          </Stack.Item>
          <Separator />
          <Stack.Item className="composite-field">
            <Label>{t('field.status.label')}</Label>
            {internalMemoSent && (
              <AutoStack horizontal={true} tokens={{ childrenGap: 8 }}>
                <Text>{`${t('Hausmitteilung versendet')}:`}</Text>
                <TimeAgo date={proving.internalMemo!.sentOn!} />
              </AutoStack>
            )}
            {!internalMemoSent && (
              <AutoStack tokens={{ childrenGap: 8 }}>
                {t('Hausmitteilung wurde noch nicht versendet')}
                <DefaultButton
                  text={t('Hausmitteilung versenden')}
                  onClick={handleSendMail(false)}
                  disabled={
                    recipientAddresses.length === 0 ||
                    isNullOrUndefined(proving.internalMemo) ||
                    isNullOrUndefined(proving.internalMemo!.memo) ||
                    isNullOrUndefined(proving.internalMemo!.memo!.uri)
                  }
                />
              </AutoStack>
            )}
          </Stack.Item>
        </FieldStack>
      </Section>
    </Form>
  )
}
